import React from "react";
import { Box, Typography, useTheme, Stack, Fade } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import CommonIconButton from "../buttons/icon-button";

interface FullPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  extraButtons?: React.ReactNode;
  showBackButton?: boolean;
}

const FullPageModal: React.FC<FullPageModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  extraButtons,
  showBackButton = true,
}) => {
  const theme = useTheme();

  if (!isOpen) return null;

  return (
    <Fade in={isOpen} timeout={300}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.nawy.spacing.md,
            paddingBottom: theme.nawy.spacing.sm,
            borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            backgroundColor: theme.palette.background.paper,
            minHeight: "64px",
            flexShrink: 0,
          }}
        >
          {/* Left side - Back button and title */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={theme.nawy.spacing.sm}
          >
            {showBackButton && (
              <CommonIconButton
                variant="outlined-secondary"
                size="md"
                onClick={onClose}
              >
                <ArrowLeft
                  size={16}
                  style={{
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                />
              </CommonIconButton>
            )}
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {title}
            </Typography>
          </Stack>

          {/* Right side - Extra buttons */}
          {extraButtons && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.nawy.spacing.xs,
              }}
            >
              {extraButtons}
            </Box>
          )}
        </Box>

        {/* Content */}

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            padding: theme.nawy.spacing.xs,
            backgroundColor: theme.nawy.colors.semantic.layer.secondary,
            height: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
    </Fade>
  );
};

export default FullPageModal;
